import React, { useState, useEffect, useCallback } from 'react';
import { addressBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Home as AddressIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import { displayLocation, normalizeLocation } from '../../utils/locationDisplay';
import PageHeader from '../../components/common/PageHeader';
import {
  formatCoord2,
  StatusSnackbars,
  CountChip,
  usePaginationState,
  useReferenceSearch,
  NoResultsRow,
  SearchBarPaper,
  CrudTablePagination,
  DeleteConfirmDialog,
  SkeletonActionCell,
  EditDeleteActions,
  makeSimpleFormChangeHandler,
  CrudFormDialog,
  LatLonFields,
  validateDecimal,
} from './_shared';

interface Address {
  id?: number;
  address: string;
  lat: string;
  lon: string;
  created_at?: string;
  updated_at?: string;
}

const AddressesPage: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState<Address>({
    address: '',
    lat: '',
    lon: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handlePasteCoordinates = useCoordinatePaste(setFormData as any, setFormErrors);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);

  const handleFormChange = makeSimpleFormChangeHandler(setFormData, setFormErrors);

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/addresses', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          sortBy: 'address',
          sortOrder: 'asc'
        }
      });

      if (response.data.success) {
        setAddresses(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch addresses');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleOpenDialog = (address?: Address) => {
    if (address) {
      setEditingAddress(address);
      // only the editable schema fields; id/created_at/updated_at would be
      // rejected by the strict update schema on PUT
      setFormData({
        address: displayLocation(address.address),
        lat: address.lat,
        lon: address.lon,
      });
    } else {
      setEditingAddress(null);
      setFormData({
        address: '',
        lat: '',
        lon: '',
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAddress(null);
    setFormErrors({});
    setFormData({
      address: '',
      lat: '',
      lon: '',
    });
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.address) newErrors.address = 'Address is required';
    const latError = validateDecimal(formData.lat, 'Latitude');
    const lonError = validateDecimal(formData.lon, 'Longitude');
    if (latError) newErrors.lat = latError;
    if (lonError) newErrors.lon = lonError;

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        address: normalizeLocation(formData.address),
        lat: Number.parseFloat(formData.lat),
        lon: Number.parseFloat(formData.lon)
      };

      if (editingAddress) {
        const response = await apiClient.put(
          `/data-management/addresses/${editingAddress.id}`,
          dataToSend
        );
        if (response.data.success) {
          setSuccess('Address updated successfully');
        }
      } else {
        preflight(addressBody, dataToSend);
        const response = await apiClient.post('/data-management/addresses', dataToSend);
        if (response.data.success) {
          setSuccess('Address created successfully');
        }
      }

      handleCloseDialog();
      fetchAddresses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save address');
    }
  };

  const handleDeleteClick = (address: Address) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!addressToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/addresses/${addressToDelete.id}`
      );
      if (response.data.success) {
        setSuccess('Address deleted successfully');
        fetchAddresses();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete address');
    } finally {
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        description="View and maintain the address reference dataset."
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchAddresses}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Address
            </Button>
          </>
        }
      />

      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      <SearchBarPaper
        placeholder="Search by address..."
        {...searchProps}
        countSlot={<CountChip loading={loading} count={totalCount} label="addresses" />}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Address</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                  <TableRow key={rowKey}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={18} height={18} />
                        <Skeleton variant="text" width={`${55 + ((idx * 11) % 30)}%`} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Skeleton variant="circular" width={16} height={16} />
                        <Skeleton variant="text" width={120} sx={{ fontSize: '0.75rem' }} />
                      </Box>
                    </TableCell>
                    <SkeletonActionCell />
                  </TableRow>
                ))
              : addresses.length === 0
                ? <NoResultsRow colSpan={3} search={search} matchMode={matchMode} />
                : addresses.map((address) => (
                  <TableRow key={address.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddressIcon fontSize="small" color="action" />
                        {displayLocation(address.address)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Lat: ${address.lat}, Lon: ${address.lon}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatCoord2(address.lat)}°, {formatCoord2(address.lon)}°
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <EditDeleteActions
                      editColor="primary"
                      onEdit={() => handleOpenDialog(address)}
                      onDelete={() => handleDeleteClick(address)}
                    />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <CrudFormDialog
        open={openDialog}
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveLabel={editingAddress ? 'Update' : 'Create'}
        saveButtonProps={{ color: 'primary' }}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleFormChange('address')}
                required
                error={!!formErrors.address}
                helperText={formErrors.address}
              />
            </Grid>
            <LatLonFields
              latValue={formData.lat}
              lonValue={formData.lon}
              onLatChange={handleFormChange('lat')}
              onLonChange={handleFormChange('lon')}
              onPaste={handlePasteCoordinates}
              latError={formErrors.lat}
              lonError={formErrors.lon}
            />
          </Grid>
      </CrudFormDialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete this address?
        <br />
        <strong>{displayLocation(addressToDelete?.address)}</strong>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default AddressesPage;
